import { Spacing } from '@persona-id/design-tokens';
import { ChevronDownIcon, ChevronUpIcon } from '@persona-id/design-tokens/icons';
import { ComponentPropsWithoutRef, FC, PropsWithChildren, ReactElement, ReactNode } from 'react';
import styled, { css } from 'styled-components';

import Header, { HeaderProps } from 'design-system/components/Card/Header';
import Spacer from 'design-system/components/Spacer';
import { Paragraph } from 'design-system/components/Text';
import { BASE } from 'design-system/variables/spacing';
import useToggle from 'lib/hooks/useToggle';

export interface CardProps {
  elevated?: boolean;
}

const Card = styled('div')<CardProps>`
  border-radius: ${({ theme }) => {
    return theme.card.borderRadius;
  }};
  display: flex;
  flex-direction: column;
  margin-bottom: ${Spacing[4]};
  background-color: ${({ theme }) => theme.color.background.primary};
  font-family: ${({ theme }) => theme.text.fontFamily.base};
  ${({ elevated }) =>
    elevated &&
    css`
      box-shadow: 0px 0px 14px 2px rgba(0, 0, 0, 0.12);
    `}
`;

/**
 * To be used within cards. Provides padding and basic layout. Should not be
 * used if card's contents are edge-to-edge with no padding.
 */
const Body = styled('section')<{ gap?: number; padding?: Spacing }>`
  margin: 0;
  padding: ${({ padding }) => padding ?? Spacing[5]};
  display: grid;
  grid-auto-flow: row;
  grid-gap: ${({ gap }) => (gap != null ? gap : BASE)}px;
  /**
    * Prevent grid blowout
    * https://css-tricks.com/preventing-a-grid-blowout/
    */
  grid-template-columns: minmax(0, 1fr);
`;

const IconContainer = styled('div')`
  margin-right: ${Spacing[3]};
  display: flex;
  align-items: center;

  > svg {
    height: 24px;
    width: 24px;
  }
`;

const ImageWrapper = styled('div')<{ height: number }>`
  width: 100%;
  overflow: hidden;
  border-radius: ${({ theme }) => theme.card.borderRadius} ${({ theme }) => theme.card.borderRadius}
    0px 0px;

  ${({ height }) => {
    return css`
      height: ${height}px;
    `;
  }};

  > img,
  svg {
    width: 100%;
    border-radius: ${({ theme }) => theme.card.borderRadius}
      ${({ theme }) => theme.card.borderRadius} 0px 0px;
  }
`;

interface TitleBaseProps {
  icon?: ReactElement;
}

interface TitleProps
  extends TitleBaseProps,
    Pick<HeaderProps, 'actions' | 'subtitle'>,
    Omit<ComponentPropsWithoutRef<'h3'>, 'color' | 'size'> {}

/**
 * Label to be used within cards. Note that some cards lead with a Title while
 * some others lead with a Subtitle (see below).
 */
const Title: FC<TitleProps> = ({ icon, children, ...elemProps }) => (
  <Header variant='title' {...elemProps}>
    {icon != null && <IconContainer>{icon}</IconContainer>}
    {children}
  </Header>
);

/**
 * Smaller label to be used within cards.
 */
const Subtitle = styled(Paragraph).attrs(() => ({ size: 'md' }))`
  color: ${({ theme }) => theme.color.text.tertiary};
`;

export interface SectionProps
  extends Pick<HeaderProps, 'subtitle'>,
    Omit<ComponentPropsWithoutRef<'h3'>, 'color' | 'size'> {}

/**
 * Header for section headers within a Card
 */
const SectionHeader = ({ children, ...elemProps }: SectionProps) => (
  <Header variant='section' {...elemProps}>
    {children}
  </Header>
);

/**
 * Wrapper for a full-width image to be used at the top of a card.
 */

interface ImageProps {
  height: number;
  children: ReactNode;
}

const Image: FC<ImageProps> = ({ height, children, ...elemProps }) => (
  <ImageWrapper {...elemProps} height={height}>
    {children}
  </ImageWrapper>
);

interface CollapsibleSectionProps {
  id?: string;
  defaultExpanded?: boolean;
  title: ReactNode;
}

const CollapsibleSection: FC<PropsWithChildren<CollapsibleSectionProps>> = ({
  id,
  defaultExpanded,
  title,
  children,
}) => {
  const [isOpen, { toggle }] = useToggle(defaultExpanded);

  return (
    <section>
      <div id={id} onClick={toggle}>
        <Header variant='section'>
          <HeaderIcon>
            {isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
            <Spacer width={0.5} height={0} />
          </HeaderIcon>
          {title}
        </Header>
      </div>
      <div>
        {isOpen && (
          <>
            <Spacer height={1} />
            {children}
          </>
        )}
      </div>
      <Spacer height={1} />
    </section>
  );
};

const HeaderIcon = styled.div`
  display: flex;
`;

export default Object.assign(Card, {
  Title,
  SectionHeader,
  Subtitle,
  Body,
  Image,
  CollapsibleSection,
});
